'use client';

import { FeatherAlertTriangle, FeatherCheck, FeatherCopy } from '@subframe/core';
import { useState } from 'react';

import { copyTextToClipboard } from '@/shared/utils';

import { Alert } from './Alert';
import { Button } from './Button';
import { Dialog } from './Dialog';

/** Textos de quem usa: o comportamento é um só, o que muda é o nome do que se mostra. */
export interface TextosDoSegredoExibido {
  titulo: string;
  instrucao: string;
  avisoSemCopiar: { titulo: string; descricao: string };
  dicaDeCopiaManual: string;
  /** Concorda com o que se copia: "Copiada" (a chave), "Copiado" (o segredo). */
  rotuloCopiado: string;
}

interface SegredoExibidoUmaVezModalProps {
  /** Texto completo. Existe só enquanto este modal está aberto: quem o chama não o guarda. */
  texto: string;
  textos: TextosDoSegredoExibido;
  onFechar: () => void;
}

/**
 * A única vez em que um segredo emitido pelo core aparece (chave de API, segredo de webhook): o
 * core guarda só o hash ou o valor cifrado. Fechar — pelo botão, pelo Esc ou clicando fora — sem
 * ter copiado pede confirmação, porque não há como ver de novo.
 *
 * `data-dd-privacy="mask"`: o RUM grava o texto da página no session replay (`MASK_USER_INPUT`
 * só mascara campos de formulário); sem a máscara o segredo iria para o Datadog.
 */
export function SegredoExibidoUmaVezModal({
  texto,
  textos,
  onFechar,
}: SegredoExibidoUmaVezModalProps) {
  const [copiada, setCopiada] = useState(false);
  const [falhaAoCopiar, setFalhaAoCopiar] = useState(false);
  const [confirmandoFechar, setConfirmandoFechar] = useState(false);

  const copiar = async () => {
    try {
      await copyTextToClipboard(texto);
      setCopiada(true);
      setFalhaAoCopiar(false);
    } catch {
      // Clipboard bloqueado pelo navegador: o texto segue na tela para cópia manual.
      setFalhaAoCopiar(true);
    }
  };

  const pedirParaFechar = () => {
    if (copiada) onFechar();
    else setConfirmandoFechar(true);
  };

  return (
    <Dialog
      open
      onOpenChange={(aberto) => {
        if (!aberto) pedirParaFechar();
      }}
    >
      <Dialog.Content className="w-full max-w-[560px] gap-0 rounded-xl p-0">
        <div className="flex w-full flex-col gap-1 border-b border-neutral-100 px-6 py-5">
          <h2 className="text-default-font text-lg font-semibold">{textos.titulo}</h2>
          <p className="text-subtext-color text-sm">{textos.instrucao}</p>
        </div>

        <div className="flex w-full flex-col gap-4 px-6 py-5">
          <div className="flex w-full items-center gap-2 rounded-md border border-solid border-neutral-200 bg-neutral-50 p-3">
            <code
              data-dd-privacy="mask"
              data-testid="segredo-exibido"
              className="text-default-font min-w-0 flex-1 font-mono text-xs break-all select-all"
            >
              {texto}
            </code>
            <Button
              variant={copiada ? 'neutral-secondary' : 'brand-primary'}
              size="small"
              icon={copiada ? <FeatherCheck /> : <FeatherCopy />}
              onClick={() => void copiar()}
            >
              {copiada ? textos.rotuloCopiado : 'Copiar'}
            </Button>
          </div>

          {falhaAoCopiar && (
            <Alert
              variant="warning"
              title="O navegador não deixou copiar"
              description={textos.dicaDeCopiaManual}
            />
          )}

          {confirmandoFechar && (
            <Alert
              variant="error"
              icon={<FeatherAlertTriangle />}
              title={textos.avisoSemCopiar.titulo}
              description={textos.avisoSemCopiar.descricao}
              actions={
                <div className="flex gap-2">
                  <Button
                    variant="neutral-secondary"
                    size="small"
                    onClick={() => setConfirmandoFechar(false)}
                  >
                    Voltar e copiar
                  </Button>
                  <Button variant="destructive-primary" size="small" onClick={onFechar}>
                    Fechar mesmo assim
                  </Button>
                </div>
              }
            />
          )}
        </div>

        <div className="flex w-full items-center justify-end gap-3 border-t border-neutral-100 px-6 py-4">
          <Button variant="neutral-secondary" onClick={pedirParaFechar}>
            Concluir
          </Button>
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
